import { useState, useMemo } from 'react';
import { Box, Flex, Text, FormLabel } from '@chakra-ui/react';
import {
    formLabelStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { Card } from '@/components/v4';
import { stringCompareIgnoreCase } from 'utils/utils';
import { isRequiredRule } from 'utils/eightByEightCatalog';
import EightByEightLicenceTreeToolbar from './EightByEightLicenceTreeToolbar';
import EightByEightLicenceTreeHeader from './EightByEightLicenceTreeHeader';
import LicenceTreeItem from './LicenceTreeItem';

// --- Data helpers ---

const buildStatusMap = (value) => {
    const map = {};
    (value || []).forEach(({ itemID, status }) => {
        map[itemID] = status;
    });
    return map;
};

const buildVolumeMap = (value) => {
    const map = {};
    (value || []).forEach(({ itemID, volume }) => {
        map[itemID] = volume ?? 0;
    });
    return map;
};

const buildOutputArray = (statusMap, volumeMap, hasVolume) =>
    Object.keys(statusMap).map((itemID) =>
        hasVolume
            ? {
                  itemID,
                  status: statusMap[itemID],
                  volume: volumeMap[itemID] ?? 0,
              }
            : { itemID, status: statusMap[itemID] },
    );

const buildChildrenOf = (catalogue) => {
    const map = {};
    catalogue.forEach((item) => {
        if (item.parentId) {
            if (!map[item.parentId]) map[item.parentId] = [];
            map[item.parentId].push(item);
        }
    });
    // Show child licences alphabetically rather than in insertion order.
    Object.values(map).forEach((children) =>
        children.sort((a, b) => stringCompareIgnoreCase(a.name, b.name)),
    );
    return map;
};

const getCollapseDescendants = (parentId, childrenOf) => {
    const collapseMap = {};
    const recurse = (id) => {
        if (childrenOf[id]?.length > 0) collapseMap[id] = true;
        (childrenOf[id] || []).forEach((c) => recurse(c.id));
    };
    recurse(parentId);
    return collapseMap;
};

const getExpandMustDescendants = (parentId, childrenOf) => {
    const expandMap = {};
    const recurse = (id) => {
        (childrenOf[id] || []).forEach((child) => {
            if (isRequiredRule(child)) {
                expandMap[child.id] = false;
                recurse(child.id);
            }
        });
    };
    recurse(parentId);
    return expandMap;
};

/**
 * LicenceTreeBase
 *
 * Shared tree for the service profile licence editor (no volume) and the
 * dashboard entitlements wizard (with volume).
 *
 * Props:
 *   catalogue    - array of catalogue items from getEightByEightCatalogue
 *   value        - array of { itemID, status[, volume] }
 *   onChange     - called with the updated array
 *   darkMode     - boolean
 *   hasVolume    - boolean: enable volume column and propagate volume to Must children
 *   hasActive    - boolean: show read-only active count column
 *   activeMap    - { [itemID]: number }: active counts per licence
 *   isReadOnly   - boolean: disable all interactive controls
 *   showFormLabel - boolean: render FormLabel + description wrapper (for form context)
 */
const LicenceTreeBase = ({
    catalogue = [],
    value = [],
    onChange,
    darkMode,
    hasVolume = false,
    hasActive = false,
    activeMap = {},
    isReadOnly = false,
    showFormLabel = false,
}) => {
    const [search, setSearch] = useState('');
    const [collapsed, setCollapsed] = useState({});

    const statusMap = useMemo(() => buildStatusMap(value), [value]);
    const volumeMap = useMemo(
        () => (hasVolume ? buildVolumeMap(value) : {}),
        [value, hasVolume],
    );

    const itemsById = useMemo(() => {
        const map = {};
        catalogue.forEach((item) => {
            map[item.id] = item;
        });
        return map;
    }, [catalogue]);

    // Partner/company-scoped catalogues return only a subtree of the full
    // platform catalogue, so the top item of that subtree can still carry a
    // real parentID pointing to an ancestor outside this list. Treat those
    // as roots too, not just items with no parentId at all.
    const rootItems = useMemo(() => {
        const catalogueIds = new Set(catalogue.map((item) => item.id));
        return catalogue
            .filter(
                (item) => !item.parentId || !catalogueIds.has(item.parentId),
            )
            .sort((a, b) => stringCompareIgnoreCase(a.name, b.name));
    }, [catalogue]);

    const childrenOf = useMemo(() => buildChildrenOf(catalogue), [catalogue]);

    const handleToggle = (item, newStatus) => {
        if (isRequiredRule(item)) return;

        const updatedStatus = { ...statusMap };
        const updatedVolume = { ...volumeMap };
        updatedStatus[item.id] = newStatus;

        if (newStatus === 'Disabled') {
            // Disabling a parent disables its whole subtree: MUST children
            // inherit the parent's status, and CAN children cannot remain
            // enabled under a disabled parent. Without cascading to CAN
            // descendants they would stay Enabled in statusMap and be saved
            // under a disabled parent — an invalid state.
            const propagateDisabled = (parentId) => {
                (childrenOf[parentId] || []).forEach((c) => {
                    updatedStatus[c.id] = 'Disabled';
                    propagateDisabled(c.id);
                });
            };
            propagateDisabled(item.id);
            setCollapsed((prev) => ({
                ...prev,
                ...getCollapseDescendants(item.id, childrenOf),
            }));
        } else {
            if (hasVolume) {
                // Propagate Enabled + parent volume to Must children
                const parentVolume = updatedVolume[item.id] ?? 0;
                const propagateMustEnabled = (parentId, vol) => {
                    (childrenOf[parentId] || [])
                        .filter((c) => isRequiredRule(c))
                        .forEach((c) => {
                            updatedStatus[c.id] = 'Enabled';
                            updatedVolume[c.id] = vol;
                            propagateMustEnabled(c.id, vol);
                        });
                };
                propagateMustEnabled(item.id, parentVolume);
            } else {
                // No-volume mode: MUST descendants auto-enable with their
                // parent; configurable (CAN) descendants reset to Disabled so
                // they must be explicitly opted in. Enabling MUST children here
                // is what writes them into statusMap as Enabled — otherwise they
                // only *display* as enabled (via resolveEffectiveStatus) and are
                // dropped from the save payload.
                const propagateStatus = (parentId, parentEnabled) => {
                    (childrenOf[parentId] || []).forEach((child) => {
                        if (isRequiredRule(child)) {
                            updatedStatus[child.id] = parentEnabled
                                ? 'Enabled'
                                : 'Disabled';
                            propagateStatus(child.id, parentEnabled);
                        } else {
                            updatedStatus[child.id] = 'Disabled';
                            propagateStatus(child.id, false);
                        }
                    });
                };
                propagateStatus(item.id, true);
            }
            // Expand the item and any Must descendants (they auto-enable; children are configurable)
            setCollapsed((prev) => ({
                ...prev,
                [item.id]: false,
                ...getExpandMustDescendants(item.id, childrenOf),
            }));
        }

        onChange(buildOutputArray(updatedStatus, updatedVolume, hasVolume));
    };

    const handleVolumeChange = (item, newVolume) => {
        if (!hasVolume || isRequiredRule(item)) return;
        const updatedVolume = { ...volumeMap, [item.id]: newVolume };
        const propagate = (parentId, vol) => {
            (childrenOf[parentId] || [])
                .filter((c) => isRequiredRule(c))
                .forEach((c) => {
                    updatedVolume[c.id] = vol;
                    propagate(c.id, vol);
                });
        };
        propagate(item.id, newVolume);
        onChange(buildOutputArray(statusMap, updatedVolume, hasVolume));
    };

    const handleAllDisabled = () => {
        const updatedStatus = {};
        catalogue.forEach((item) => {
            updatedStatus[item.id] = 'Disabled';
        });
        const newCollapsed = {};
        catalogue.forEach((item) => {
            if (childrenOf[item.id]?.length > 0) newCollapsed[item.id] = true;
        });
        setCollapsed(newCollapsed);
        onChange(buildOutputArray(updatedStatus, volumeMap, hasVolume));
    };

    const handleAllEnabled = () => {
        const updatedStatus = {};
        catalogue.forEach((item) => {
            updatedStatus[item.id] = 'Enabled';
        });
        setCollapsed({});
        onChange(buildOutputArray(updatedStatus, volumeMap, hasVolume));
    };

    const toggleCollapse = (id) => {
        setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const matchesSearch = (item) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            item.name.toLowerCase().includes(q) ||
            item.description?.toLowerCase().includes(q)
        );
    };

    const hasMatchingDescendant = (parentId) =>
        (childrenOf[parentId] || []).some(
            (c) => matchesSearch(c) || hasMatchingDescendant(c.id),
        );

    const allAreDisabled =
        catalogue.length > 0 &&
        catalogue.every((item) => statusMap[item.id] === 'Disabled');

    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const rowBg = darkMode ? 'gray.800' : 'white';
    const headerBg = darkMode ? 'gray.700' : '#FCFCFC';
    const searchbarBg = darkMode ? 'gray.700' : '#EEE';
    const textColor = darkMode ? 'white' : 'gray.800';

    const visibleRootItems = rootItems.filter(
        (item) => matchesSearch(item) || hasMatchingDescendant(item.id),
    );

    const tree = (
        <Card overflow="hidden">
            <Box px={4} pt={4} pb={2} bg={searchbarBg}>
                <EightByEightLicenceTreeToolbar
                    onAllDisabled={handleAllDisabled}
                    onAllEnabled={handleAllEnabled}
                    allDisabled={allAreDisabled}
                    search={search}
                    onSearchChange={setSearch}
                    darkMode={darkMode}
                    borderColor={borderColor}
                />
            </Box>

            <EightByEightLicenceTreeHeader
                headerBg={headerBg}
                borderColor={borderColor}
                textColor={textColor}
                hasVolume={hasVolume}
                hasActive={hasActive}
            />

            <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderTopWidth={0}
                overflow="hidden">
                {visibleRootItems.map((item) => (
                    <LicenceTreeItem
                        key={item.id}
                        item={item}
                        depth={0}
                        statusMap={statusMap}
                        volumeMap={volumeMap}
                        activeMap={activeMap}
                        itemsById={itemsById}
                        childrenOf={childrenOf}
                        collapsed={collapsed}
                        onToggle={handleToggle}
                        onVolumeChange={handleVolumeChange}
                        onToggleCollapse={toggleCollapse}
                        darkMode={darkMode}
                        borderColor={borderColor}
                        rowBg={rowBg}
                        textColor={textColor}
                        search={search}
                        hasVolume={hasVolume}
                        hasActive={hasActive}
                        isReadOnly={isReadOnly}
                    />
                ))}
                {catalogue.length === 0 && (
                    <Flex justify="center" py={6}>
                        <Text fontSize="sm" color="#757575">
                            No licences available.
                        </Text>
                    </Flex>
                )}
            </Box>
        </Card>
    );

    if (!showFormLabel) return tree;

    return (
        <Box sx={{ width: '775px', marginBottom: '24px' }}>
            <Box mb={3}>
                <FormLabel sx={formLabelStyling(darkMode)}>
                    Available Licences{' '}
                    <span style={{ color: '#e53e3e' }}>*</span>
                </FormLabel>
                <Text sx={descriptionStyling} maxWidth={500}>
                    Configure which licences are enabled or disabled for this
                    profile. Mandatory child items inherit their parent&apos;s
                    status automatically.
                </Text>
            </Box>
            {tree}
        </Box>
    );
};

export default LicenceTreeBase;
